import { useMemo, useRef, useState } from 'react';

import type { ChainAddressId } from '@infinex/asset-config';
import { makeChainAddressId } from '@infinex/asset-config';
import { getPreferredGasAccountAssets } from '@infinex/gas-account';
import { MINIMUM_GAS_ACCOUNT_TOPUP } from '@infinex/main-service-contract/v2/client/gas';
import { cn, useIsSmallScreen } from '@infinex/ui-styles';
import {
  Content,
  LinkButton,
  ProgressCircle,
  SearchField,
  useDialogRouteState,
} from '@infinex/ui/components';

import { useBalanceFiltering } from '../../../api/assets/hooks/useBalanceFiltering';
import type { CustodyDisplayFilterId } from '../../../api/assets/hooks/useCustodyDisplayItems';
import { SwidgeEmptyStateGraphic } from '../../../assets';
import { useUnifiedBalances } from '../../../features/balances';
import type { ChainAssetBalanceSummed } from '../../../features/balances';
import { isNativeWebView } from '../../../lib/mobile';
import type { FullAsset } from '../../../types';
import { ChainTokenBalanceList } from '../../asset-select';
import type { AssetSelection } from '../../asset-select/type';
import { AssetEmptyState } from '../../AssetEmptyState';
import { makeTestId } from '../../makeTestId';
import type { CustodySelected } from '../../wallet-selector/WalletSelectedProvider';
import { useCustodySelection } from '../../wallet-selector/WalletSelectedProvider';
import { useGasDeposit } from '../GasDepositQuoteProvider';
import { useGasDialogConfig } from '../GasDialogDependenciesContext';

export const GasAssetSelector = ({
  onSelectAssetSource,
}: {
  onSelectAssetSource?: (asset: AssetSelection) => void;
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const scrollElementRef = useRef<HTMLDivElement | null>(null);

  const { setSelected } = useCustodySelection();
  const { onGoToWallet } = useGasDialogConfig();

  const {
    assetSelection: { selectedAssetDetails, setSelectedChainId },
  } = useGasDeposit();

  const routerState = useDialogRouteState();

  // Show balances across ALL wallets, the wallet will be selected in the GasAssetSourceScreen
  const { balancesByChainAddressId = {} } = useUnifiedBalances();
  const isSmallScreen = useIsSmallScreen();

  const {
    isLoading,
    value: { filteredTokens: filteredWalletBalances },
  } = useBalanceFiltering({
    search: searchQuery,
    minimumSupportLevel: 'full',
  });

  const preferredAssets = useMemo(
    () =>
      getPreferredGasAccountAssets(
        Object.values(balancesByChainAddressId) as ChainAssetBalanceSummed[]
      ),
    [balancesByChainAddressId]
  );

  const checkIsDisabled = (
    chainAddressId: ChainAddressId
  ):
    | {
        disabledLabel?: string;
        disabledDetails?: string;
      }
    | false => {
    if (!preferredAssets.find((a) => a.id === chainAddressId)) {
      return {
        disabledLabel: 'Unsupported asset',
        disabledDetails: 'Top-ups from this asset are not currently supported.',
      };
    }

    if (
      chainAddressId &&
      (balancesByChainAddressId[chainAddressId]?.balanceUsd ?? 0) <=
        MINIMUM_GAS_ACCOUNT_TOPUP
    ) {
      return {
        disabledLabel: 'Insufficient balance',
        disabledDetails:
          'This asset does not have sufficient balance to top up gas.',
      };
    }

    return false;
  };

  // Transform ChainAsset to format expected by SwidgeAssetSelectDialog
  const transformedSelectedAsset: FullAsset | undefined = useMemo(() => {
    if (!selectedAssetDetails) return undefined;

    return {
      assetGroupId: selectedAssetDetails.assetId,
      chain: selectedAssetDetails.chain,
      address: selectedAssetDetails.assetAddress,
      assetType: 'token',
      decimals: selectedAssetDetails.decimals,
    };
  }, [selectedAssetDetails]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex w-full flex-col items-center justify-center p-6">
          <ProgressCircle
            aria-label="Loading"
            prominence="low"
            isIndeterminate
            size="large"
          />
        </div>
      );
    }

    if (!Object.entries(filteredWalletBalances).length) {
      return (
        <AssetEmptyState
          imgSrc={SwidgeEmptyStateGraphic}
          title="Looks a little empty in here"
          description={'Deposit assets to your wallet to get started.'}
          actionElement={
            onGoToWallet ? (
              <LinkButton onPress={onGoToWallet}>Go to Wallet</LinkButton>
            ) : undefined
          }
        />
      );
    }

    return (
      <ChainTokenBalanceList
        buildTestId={makeTestId}
        sectionTitle="Tokens"
        listClassName="py-4"
        tokenBalances={filteredWalletBalances}
        scrollElementRef={scrollElementRef}
        hideSectionTitle
        hideSmallBalances={false}
        selectedAsset={transformedSelectedAsset}
        showWalletSources
        onSelection={({ chain, address, sources, ...rest }) => {
          setSelectedChainId(makeChainAddressId(chain, address));
          if (sources.length === 1) {
            const custodySelected = toCustodySelected(sources[0].id);
            if (custodySelected) {
              setSelected(custodySelected);
              routerState?.pop();
            }
          } else {
            onSelectAssetSource?.({ chain, address, sources, ...rest });
          }
        }}
        checkIsDisabled={checkIsDisabled}
      />
    );
  };

  return (
    <>
      <div className="px-(--Dialog-padding) pt-(--Dialog-padding)">
        <SearchField
          aria-label="Search for a token"
          placeholder="Search for a token"
          value={searchQuery}
          onChange={setSearchQuery}
          size="large"
          autoComplete="off"
        />
      </div>

      <Content
        ref={scrollElementRef}
        className={cn(
          'flex flex-col gap-y-0 px-[calc(var(--Dialog-padding)-16px)]',
          { 'max-h-[600px]': !isSmallScreen && !isNativeWebView() }
        )}
      >
        {renderContent()}
      </Content>
    </>
  );
};

/**
 * Converts CustodyDisplayFilterId format to CustodySelected format
 * TODO: Remove when https://linear.app/infinex-app/issue/ENG-6330/refactor-to-one-type-for-custody-selection
 */
export function toCustodySelected(
  filterId: CustodyDisplayFilterId
): CustodySelected | undefined {
  if (filterId === 'all') return undefined;
  if (filterId === 'vault') return { custody: 'vault' };
  const walletId = filterId.replace('wallet:', '');
  // For now, treating all non-vault custodies as wallet
  // The actual custody type is determined when looking up the wallet
  return { custody: 'wallet', id: walletId };
}
